<template>
	<q-dialog v-model="dialogOpen" :persistent="isOrphan">
		<q-card class="join-card">
			<q-card-section>
				<div class="text-h6" style="color: #ff6600">
					{{ isOrphan ? 'Orphaned Nodes' : isUnjoin ? 'Unjoin From...' : isFlip ? 'Flip Arrow With...' : isSetParent ? 'Set Parent To...' : isNodeJoin ? 'Join Node To...' : 'Join Mission To...' }}
				</div>
				<div v-if="isOrphan" class="text-caption" style="color: #aaa">
					"{{ subjectTitle }}" and its children are no longer connected to a mission.
					Join them under a node, keep them as a new mission, or delete them.
				</div>
				<div v-else-if="isUnjoin" class="text-caption" style="color: #aaa">
					Pick a connected node; the link between it and "{{ subjectTitle }}" is removed.
				</div>
				<div v-else-if="isFlip" class="text-caption" style="color: #aaa">
					Pick a connected node; the arrow between it and "{{ subjectTitle }}" reverses.
				</div>
				<div v-else-if="isSetParent" class="text-caption" style="color: #aaa">
					Pick a node; it replaces the current parent(s) of "{{ subjectTitle }}".
				</div>
				<div v-else-if="isNodeJoin" class="text-caption" style="color: #aaa">
					Pick a node; it becomes an additional parent of "{{ subjectTitle }}".
				</div>
				<div v-else class="text-caption" style="color: #aaa">
					Pick a mission and a node in it; that node becomes the new parent of
					"{{ sourceTitle }}" and the two missions merge.
				</div>
			</q-card-section>

			<q-card-section>
				<q-select
					v-if="!isNeighbourPick"
					v-model="targetGraphId"
					dark
					filled
					dense
					:options="graphOptions"
					option-value="id"
					option-label="name"
					emit-value
					map-options
					label="Mission"
					class="q-mb-md"
					@update:model-value="onGraphPicked"
				/>
				<q-select
					v-model="targetNodeId"
					dark
					filled
					dense
					:options="nodeOptions"
					option-value="id"
					:option-label="nodeLabel"
					emit-value
					map-options
					:label="isNeighbourPick ? 'Connected Node' : 'Parent Node'"
					:disable="!targetGraphId"
					:loading="loadingNodes"
					class="q-mb-md"
				/>
				<q-input
					v-if="isOrphan"
					v-model="newGraphName"
					dark
					filled
					dense
					label="New mission name (for 'As New Graph')"
				/>
			</q-card-section>

			<div class="dialog-actions">
				<ScifiButton v-if="!isOrphan" text="CANCEL" buttonType="normal" @click="onCancel" />
				<ScifiButton v-if="isOrphan" text="DELETE" confirmText="CONFIRM" buttonType="negative" @click="onDeleteOrphan" />
				<ScifiButton v-if="isOrphan" text="AS NEW GRAPH" buttonType="warning" @click="onNewGraph" />
				<ScifiButton v-if="isUnjoin" text="UNJOIN" buttonType="warning" @click="onJoin" />
				<ScifiButton v-else :text="isFlip ? 'FLIP' : isSetParent ? 'SET PARENT' : 'JOIN'" buttonType="positive" @click="onJoin" />
			</div>
		</q-card>
	</q-dialog>
</template>

<script setup lang="ts">
	import { ref, computed, watch } from 'vue';
	import { useQuasar } from 'quasar';
	import { useGraphNodeStore } from '../stores/graphNodes';
	import { type GNode } from '../models/graphNode';

	const graphNodeStore = useGraphNodeStore();
	const $q = useQuasar();

	const targetGraphId = ref('');
	const targetNodeId = ref('');
	const newGraphName = ref('');
	const nodeOptions = ref<GNode[]>([]);
	const loadingNodes = ref(false);

	const isOrphan = computed(() => graphNodeStore.joinDialog.mode === 'orphan');
	const isNodeJoin = computed(() => graphNodeStore.joinDialog.mode === 'node');
	const isSetParent = computed(() => graphNodeStore.joinDialog.mode === 'setparent');
	const isFlip = computed(() => graphNodeStore.joinDialog.mode === 'flip');
	const isUnjoin = computed(() => graphNodeStore.joinDialog.mode === 'unjoin');
	// Node-scoped picker (within the tree): both "add parent" and "set parent".
	const isNodeLike = computed(() => isNodeJoin.value || isSetParent.value);
	// Picker lists the subject's connected neighbours (flip + unjoin).
	const isNeighbourPick = computed(() => isFlip.value || isUnjoin.value);
	const subjectNodeId = computed(() => graphNodeStore.joinDialog.subjectNodeId);

	const subjectTitle = computed(() => {
		const node = graphNodeStore.findLocalNode(subjectNodeId.value);
		return node?.title || 'Untitled node';
	});

	const sourceTitle = computed(() => {
		const graph = graphNodeStore.graphs.find((g) => g.id === graphNodeStore.currentGraphId);
		return graph?.name || 'this mission';
	});

	// Joining a mission into itself makes no sense; orphan and node joins may
	// target any mission (node joins default to the current one).
	const graphOptions = computed(() => {
		if (graphNodeStore.joinDialog.mode === 'join') {
			return graphNodeStore.graphs.filter((g) => g.id !== graphNodeStore.currentGraphId);
		}
		return graphNodeStore.graphs;
	});

	const nodeLabel = (node: GNode) => {
		return node.type ? `${node.title || 'Untitled'} (${node.type})` : node.title || 'Untitled';
	};

	const dialogOpen = computed({
		get: () => graphNodeStore.joinDialog.open,
		set: (open: boolean) => {
			if (!open) {
				graphNodeStore.closeJoinDialog();
			}
		},
	});

	watch(
		() => graphNodeStore.joinDialog.open,
		async (open) => {
			if (!open) {
				return;
			}
			targetNodeId.value = '';
			nodeOptions.value = [];
			newGraphName.value = isOrphan.value ? subjectTitle.value : '';
			// Orphan and node joins default to the current mission; a mission
			// join excludes the current graph, so take the first remaining.
			const fallback = graphOptions.value[0]?.id ?? '';
			const preferred = isOrphan.value || isNodeLike.value || isNeighbourPick.value
				? graphOptions.value.find((g) => g.id === graphNodeStore.currentGraphId)?.id ?? fallback
				: fallback;
			targetGraphId.value = preferred;
			if (preferred) {
				await onGraphPicked(preferred);
			}
		}
	);

	const onGraphPicked = async (graphId: string) => {
		targetNodeId.value = '';
		loadingNodes.value = true;
		// Flip/Unjoin list only the subject's current neighbours.
		if (isNeighbourPick.value) {
			const subject = await graphNodeStore.getNodeById(subjectNodeId.value);
			const neighbourIds = [...(subject?.parents ?? []), ...(subject?.children ?? [])];
			const neighbours: GNode[] = [];
			for (const id of neighbourIds) {
				const n = await graphNodeStore.getNodeById(id);
				if (n) {
					neighbours.push(n);
				}
			}
			nodeOptions.value = neighbours;
			loadingNodes.value = false;
			return;
		}
		let nodes = await graphNodeStore.listGraphNodes(graphId);
		if (isNodeLike.value) {
			// A node can't parent to itself, its own descendants (cycle), or a
			// node that's already its parent.
			const subject = graphNodeStore.findLocalNode(subjectNodeId.value);
			const byId = new Map(nodes.map((n) => [n.id, n]));
			const excluded = new Set([subjectNodeId.value]);
			const queue = [subjectNodeId.value];
			while (queue.length > 0) {
				const next = byId.get(queue.shift() as string);
				for (const childId of next?.children ?? []) {
					if (!excluded.has(childId)) {
						excluded.add(childId);
						queue.push(childId);
					}
				}
			}
			nodes = nodes.filter((n) => !excluded.has(n.id) && !(subject?.parents ?? []).includes(n.id));
		}
		nodeOptions.value = nodes;
		loadingNodes.value = false;
	};

	const onJoin = async () => {
		if (!targetGraphId.value || !targetNodeId.value) {
			return;
		}
		// Actions that can be refused (cycle) return false; keep the dialog open
		// and warn instead of silently closing.
		let ok = true;
		if (isOrphan.value) {
			ok = await graphNodeStore.resolveOrphanJoin(subjectNodeId.value, targetGraphId.value, targetNodeId.value);
		} else if (isUnjoin.value) {
			await graphNodeStore.unjoinEdge(subjectNodeId.value, targetNodeId.value);
		} else if (isFlip.value) {
			ok = await graphNodeStore.flipEdge(subjectNodeId.value, targetNodeId.value);
		} else if (isSetParent.value) {
			ok = await graphNodeStore.resolveNodeReparent(subjectNodeId.value, targetNodeId.value);
		} else if (isNodeJoin.value) {
			ok = await graphNodeStore.resolveNodeJoin(subjectNodeId.value, targetNodeId.value);
		} else {
			ok = await graphNodeStore.joinCurrentGraphTo(targetGraphId.value, targetNodeId.value);
		}
		if (!ok) {
			$q.notify({ type: 'warning', message: 'That would create a loop in the graph.' });
			return;
		}
		graphNodeStore.closeJoinDialog();
	};

	const onDeleteOrphan = async () => {
		await graphNodeStore.resolveOrphanDelete(subjectNodeId.value);
		graphNodeStore.closeJoinDialog();
	};

	const onNewGraph = async () => {
		await graphNodeStore.resolveOrphanNewGraph(subjectNodeId.value, newGraphName.value);
		graphNodeStore.closeJoinDialog();
	};

	const onCancel = () => {
		graphNodeStore.closeJoinDialog();
	};
</script>

<style scoped>
	.join-card {
		background: #050a0e;
		color: #00ffea;
		border: 2px solid #00ffea;
		box-shadow: 0 0 12px #00ffea20;
		border-radius: 8px;
		min-width: 480px;
	}

	.dialog-actions {
		display: flex;
		justify-content: space-around;
		padding: 12px;
		border-top: 1px solid #00ffea20;
		background: #0a1418;
	}
</style>
